// Thin Recharts line wrapper: 2px lines, recessive grid, crosshair tooltip, legend only for two
// or more series, colours from the chart tokens in fixed order.
import { CartesianGrid, Line, LineChart, XAxis, YAxis } from "recharts";

import { cn } from "../cn";
import {
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
} from "../components/chart";
import { type SeriesSpec, seriesConfig } from "./series";

export type TrendLineChartProps = {
  data: readonly Record<string, unknown>[];
  xKey: string;
  series: readonly SeriesSpec[];
  /** Accessible name for the chart (the title is the label for a single series). */
  label: string;
  className?: string;
  /** Dashed reference series key (e.g. target) drawn in the muted foreground. */
  referenceKey?: string;
};

export function TrendLineChart({
  data,
  xKey,
  series,
  label,
  className,
  referenceKey,
}: TrendLineChartProps) {
  const config = seriesConfig(series);
  return (
    <ChartContainer
      config={config}
      role="img"
      aria-label={label}
      className={cn("aspect-auto h-48 w-full", className)}
    >
      <LineChart
        data={data as Record<string, unknown>[]}
        margin={{ top: 8, right: 8, bottom: 0, left: 0 }}
      >
        <CartesianGrid vertical={false} stroke="var(--chart-grid)" />
        <XAxis dataKey={xKey} tickLine={false} axisLine={false} tickMargin={8} minTickGap={16} />
        <YAxis tickLine={false} axisLine={false} width={40} />
        <ChartTooltip cursor content={<ChartTooltipContent indicator="line" />} />
        {series.length > 1 ? <ChartLegend content={<ChartLegendContent />} /> : null}
        {referenceKey ? (
          <Line
            dataKey={referenceKey}
            stroke="var(--muted-foreground)"
            strokeDasharray="4 4"
            strokeWidth={1.5}
            dot={false}
            isAnimationActive={false}
          />
        ) : null}
        {series.map((s) => (
          <Line
            key={s.key}
            dataKey={s.key}
            name={s.label}
            type="monotone"
            stroke={`var(--color-${s.key})`}
            strokeWidth={2}
            dot={{ r: 3 }}
            activeDot={{ r: 5 }}
            isAnimationActive={false}
            connectNulls={false}
          />
        ))}
      </LineChart>
    </ChartContainer>
  );
}
